/* eslint-disable no-use-before-define, @typescript-eslint/no-use-before-define, no-param-reassign, no-plusplus, no-nested-ternary, no-mixed-operators, no-void, no-continue, prefer-destructuring, no-bitwise */
// Multi Internet, the six-connection instrument — a port of the approved component's script (an
// <iframe srcdoc> before the design went native). The section renders the resting markup; this module
// hydrates it: the six rows carry the providers of the visitor's country, the auto-demo tells its story
// (pair → +1 → one fails → failover → back → …), every card toggles, "Simular falha" drops the fastest
// link, packets travel the wires (Web Animations), the throughput sparklines breathe.
//
// Same contract as ./multiconnection.ts: lookups stay under `root`, copy comes from `strings`, the
// returned function tears everything down. The lint rules switched off above are the ones a
// line-for-line port of the imperative engine trips on.
import { askVisitorGeo, forcedNetwork, type GeoData, normalizeNetwork } from './geo';
import type { MiStrings } from './strings';

interface Box {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface Packet {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface Segment {
  d: string;
  parks: number[];
  packets: Packet[];
  frames: Keyframe[] | null;
  parkIdx: number[] | null;
  hide?: { l: number; t: number; r: number; b: number }[] | null;
  len?: number;
  offT?: number;
}

interface Conn {
  id: string;
  name: string;
  sr: string;
  brand: string;
  kind: string;
  mbps: number;
  el: HTMLButtonElement;
  ccEl: HTMLElement;
  tagEl: HTMLElement;
  mbEl: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement;
  spFill: SVGPolygonElement;
  spDot: SVGCircleElement;
  on: boolean;
  down: boolean;
  wasOn: boolean;
  wasDown?: boolean;
  offT: number;
  hist: number[];
  g?: SVGGElement;
  pg?: SVGGElement;
  set?: HTMLDivElement;
  segIn?: Segment;
  segOut?: Segment;
  baseIn?: SVGPathElement;
  baseOut?: SVGPathElement;
  glowIn?: SVGPathElement;
  glowOut?: SVGPathElement;
  hotIn?: SVGPathElement;
  hotOut?: SVGPathElement;
  cut?: SVGGElement;
  portIn?: SVGCircleElement;
  portOut?: SVGCircleElement;
  stubD?: string;
  cutXY?: string;
  busR?: { x: number; tile: Box; card: Box } | null;
}

interface Trunk extends Segment {
  g: SVGGElement | null;
  set: HTMLDivElement | null;
  base?: SVGPathElement;
  glow?: SVGPathElement;
  hot?: SVGPathElement;
  wasOn: boolean | null;
  live: number;
}

interface ProviderPick {
  brand: string;
  kind: string;
  mine?: boolean;
}

export interface MiOptions {
  root: HTMLElement;
  geo: GeoData;
  strings: MiStrings;
}

const NS = 'http://www.w3.org/2000/svg';
const SP_W = 52;
const SP_H = 18;
const HIST_N = 8;
const BASE_MS = 1600;
const TRUNK_PHASES_DESK = [0.12, 0.55];
const TRUNK_PHASES_BUS = [0.03, 0.3, 0.5, 0.82];
const USER_IDLE = 8000;
const BEND_A = 0.15;
const BEND_B = 0.27;
const PORT_D = 5.25;
const JACK_D = 4;

const BASE_CONNS: { id: string; name: string; mbps: number }[] = [
  { id: 'fibra', name: 'Fibra', mbps: 500 },
  { id: 'wifi', name: 'Wi-Fi 6', mbps: 300 },
  { id: 'g5', name: '5G', mbps: 150 },
  { id: 'cabo', name: 'Cabo', mbps: 250 },
  { id: 'isp2', name: '2', mbps: 200 },
  { id: 'g4', name: '4G', mbps: 60 },
];

function fold(text: string | undefined | null) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}
function f(n: number) {
  return Math.round(n * 10) / 10;
}
function esc(text: string) {
  return text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}
/** product terms never break across lines ("Wi-Fi 6", "Claro 4G") */
const nb = (text: string) => `<span class="nb">${esc(text)}</span>`;
/** long / short wording, picked by the stage's own container branch */
const alt = ([long, short]: [string, string]) => `<span class="x-long">${long}</span><span class="x-short">${short}</span>`;

/** slot → provider for a country: the visitor's ISP first (when known), fibre, cable and the other fixed providers, then the mobile operators */
export function pickProviders(data: GeoData, cc: string, isp: string, S: Pick<MiStrings, 'kind' | 'kind5g' | 'kind4g' | 'kindWifi'>): Record<string, ProviderPick> {
  const P = data.providers[cc] || data.providers.US || {};
  const used: Record<string, number> = {};
  const out: Record<string, ProviderPick> = {};
  const fixed = P.fixed || [];
  const mobile = P.mobile || [];
  type Entry = [string, string | boolean];
  const take = (entries: Entry[], ok?: (entry: Entry) => boolean): Entry | null => {
    const hit = entries.find((entry) => !used[fold(entry[0])] && (!ok || ok(entry)));
    if (!hit) return null;
    used[fold(hit[0])] = 1;
    return hit;
  };
  const any = (entries: Entry[], index: number): Entry | null => (entries.length ? entries[index % entries.length]! : null);
  let mine: Entry | null = null;
  const folded = fold(isp);
  if (folded) {
    ([...fixed, ...mobile] as Entry[]).forEach((entry) => {
      const key = fold(entry[0]);
      if (!mine && key && (folded.indexOf(key) >= 0 || key.indexOf(folded) >= 0)) mine = entry;
    });
    if (mine) used[fold((mine as Entry)[0])] = 1;
  }
  const isMobile = (entry: Entry) => (mobile as Entry[]).indexOf(entry) >= 0;
  const kindOf = (entry: Entry) => (isMobile(entry) ? (entry[1] === true ? S.kind5g : S.kind4g) : S.kind[entry[1] as keyof MiStrings['kind']] || S.kind.fiber);
  const fixedList = fixed as Entry[];
  const mobileList = mobile as Entry[];
  const fib = mine || take(fixedList, (entry) => entry[1] === 'fiber') || take(fixedList) || any(fixedList, 0) || any(mobileList, 0) || ['NoPing', 'fiber'];
  out.fibra = { brand: fib[0], kind: kindOf(fib), mine: !!mine };
  const cab = take(fixedList, (entry) => entry[1] === 'cable') || take(fixedList) || any(fixedList, 1) || fib;
  out.cabo = { brand: cab[0], kind: kindOf(cab) };
  const two = take(fixedList) || any(fixedList, 2) || cab;
  out.isp2 = { brand: two[0], kind: kindOf(two) };
  const wf = take(fixedList) || any(fixedList, 3) || fib;
  out.wifi = { brand: wf[0], kind: isMobile(wf) ? kindOf(wf) : S.kindWifi };
  /* mobile rows: a brand already on a fixed row is fine, the same operator twice is not */
  const m5 = take(mobileList, (entry) => entry[1] === true) || take(mobileList) || any(mobileList, 0) || fib;
  out.g5 = { brand: m5[0], kind: isMobile(m5) && m5[1] === true ? S.kind5g : S.kind4g };
  const m4 = take(mobileList) || mobileList.filter((entry) => entry !== m5)[0] || any(mobileList, 1) || m5;
  out.g4 = { brand: m4[0], kind: S.kind4g };
  return out;
}

export function initMultiInternet({ root, geo: GEO, strings: S }: MiOptions): () => void {
  const q = <T extends Element>(selector: string) => root.querySelector<T>(selector)!;
  const disposers: (() => void)[] = [];
  const timers = new Set<number>();
  let destroyed = false;
  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!destroyed) fn();
    }, ms);
    timers.add(id);
    return id;
  };
  const clearLater = (id: number) => {
    if (!id) return;
    window.clearTimeout(id);
    timers.delete(id);
  };
  const listen = (target: EventTarget, type: string, handler: (event: any) => void) => {
    target.addEventListener(type, handler);
    disposers.push(() => target.removeEventListener(type, handler));
  };

  root.classList.add('is-js');
  const stage = q<HTMLElement>('[data-stage]');
  const svg = q<SVGSVGElement>('[data-wires]');
  const pkLayer = q<HTMLElement>('[data-packets]');
  const list = q<HTMLElement>('[data-routes]');
  const pcNode = q<HTMLElement>('[data-node="pc"]');
  const npNode = q<HTMLElement>('[data-node="np"]');
  const gameNode = q<HTMLElement>('[data-node="game"]');
  const pcIcon = pcNode.querySelector<HTMLElement>('.mc-node-icon')!;
  const npIcon = npNode.querySelector<HTMLElement>('.mc-node-icon')!;
  const gameIcon = gameNode.querySelector<HTMLElement>('.mc-node-icon')!;
  const npSub = q<HTMLElement>('[data-np-sub]');
  const gameSub = q<HTMLElement>('[data-game-sub]');
  const statusText = q<HTMLElement>('[data-status-text]');
  const liveEl = q<HTMLElement>('[data-live]');
  const bridge = q<HTMLElement>('[data-bridge]');
  const dropBtn = q<HTMLElement>('[data-drop]');
  const dropLbl = dropBtn.querySelector<HTMLElement>('[data-lbl]')!;
  const pauseBtn = q<HTMLElement>('[data-pause]');
  const hintText = q<HTMLElement>('[data-hint]');
  const keyEl = q<HTMLElement>('[data-key]');
  const countEl = q<HTMLElement>('[data-count]');
  const countW = q<HTMLElement>('[data-count-w]');
  const backupSub = q<HTMLElement>('[data-backup-sub]');
  const backupB = q<HTMLElement>('[data-backup-b]');
  const pBase = q<HTMLElement>('[data-p-base]');
  const pBaseU = q<HTMLElement>('[data-p-base-u]');
  const pSum = q<HTMLElement>('[data-p-sum]');
  const pSumU = q<HTMLElement>('[data-p-sum-u]');
  const pPct = q<HTMLElement>('[data-p-pct]');
  const pFill = q<HTMLElement>('[data-p-fill]');
  const pFillGood = q<HTMLElement>('[data-p-fill-good]');
  const pDrops = q<HTMLElement>('[data-p-drops]');
  const proofSr = root.querySelector<HTMLElement>('[data-proof-sr]');
  const sumEl = q<HTMLElement>('[data-sum]');
  const sumU = q<HTMLElement>('[data-sum-u]');
  const sumStrong = sumEl.parentElement!;
  const sumB = q<HTMLElement>('[data-sum-b]');
  const sumSub = q<HTMLElement>('[data-sum-sub]');
  const sumRow = q<HTMLElement>('[data-sum-row]');
  const srNow = root.querySelector<HTMLElement>('[data-sr-now]');
  const miDesc = root.querySelector<HTMLElement>('[data-mi-desc]');
  let resultNote: string | null = null;
  /* the match's drop counter: it goes to 1 only when a lone connection fails (no reserve), and it stays there until a second connection is active */
  let kicked = false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mc-mobile').trim() === '1';

  function fmtCap(mbps: number) {
    if (!mbps) return { n: '-', u: '', sr: S.noBandwidth };
    if (mbps < 1000) return { n: String(mbps), u: S.mbps, sr: S.srMbps(String(mbps)) };
    const txt = S.decimal(Math.round(mbps / 10) / 100);
    return { n: txt, u: S.gbps, sr: S.srGbps(txt) };
  }

  const CONNS: Conn[] = BASE_CONNS.map((base) => ({ ...base, sr: base.name, brand: base.name, kind: '' }) as Conn);
  function applyProviders(sel: Record<string, ProviderPick>) {
    const count: Record<string, number> = {};
    CONNS.forEach((conn) => {
      const pick = sel[conn.id];
      if (pick) count[pick.brand] = (count[pick.brand] || 0) + 1;
    });
    CONNS.forEach((conn) => {
      const pick = sel[conn.id];
      if (!pick) return;
      conn.brand = pick.brand;
      conn.kind = pick.kind + (pick.mine ? S.yoursSuffix : '');
      conn.name = (count[pick.brand] ?? 0) > 1 ? `${pick.brand} ${pick.kind}` : pick.brand;
      conn.sr = `${pick.brand} ${pick.kind}${pick.mine ? S.yoursSr : ''}`;
    });
  }
  const forced = forcedNetwork();
  applyProviders(pickProviders(GEO, forced?.country || 'US', forced?.isp || '', S));
  const TOTAL = CONNS.length;
  CONNS.forEach((conn) => {
    conn.el = list.querySelector<HTMLButtonElement>(`[data-conn="${conn.id}"]`)!;
    conn.ccEl = conn.el.querySelector<HTMLElement>('.mc-route-cc')!;
    conn.tagEl = conn.el.querySelector<HTMLElement>('[data-tag]')!;
    conn.mbEl = conn.el.querySelector<HTMLElement>('[data-mb]')!;
    conn.deltaEl = conn.el.querySelector<HTMLElement>('[data-delta]')!;
    conn.spLine = conn.el.querySelector<SVGPolylineElement>('.mc-route-spark polyline')!;
    conn.spFill = conn.el.querySelector<SVGPolygonElement>('.mc-route-spark .sp-fill')!;
    conn.spDot = conn.el.querySelector<SVGCircleElement>('.mc-route-spark .sp-dot')!;
    conn.on = conn.el.classList.contains('is-on');
    conn.down = false;
    conn.wasOn = conn.on;
    conn.offT = 0;
    conn.hist = [];
  });
  /* ---------- throughput sparkline ---------- */
  const spLevel = (conn: Conn) => (conn.down ? 0 : conn.on ? 0.78 : 0.14);
  function spSample(conn: Conn) {
    const level = spLevel(conn);
    if (!level) return 0;
    const amp = conn.on ? 0.16 : 0.05;
    return Math.max(0.04, Math.min(0.96, level + (Math.random() * 2 - 1) * amp));
  }
  function seedHist(conn: Conn) {
    conn.hist = [];
    for (let i = 0; i < HIST_N; i++) conn.hist.push(spSample(conn));
  }
  function drawSpark(conn: Conn) {
    const pts: string[] = [];
    const n = conn.hist.length;
    for (let i = 0; i < n; i++) pts.push(`${f(1.5 + ((SP_W - 3) * i) / (HIST_N - 1))},${f(SP_H - 1.5 - (SP_H - 3) * conn.hist[i]!)}`);
    conn.spLine.setAttribute('points', pts.join(' '));
    conn.spFill.setAttribute('points', `1.5,${SP_H} ${pts.join(' ')} ${f(1.5 + ((SP_W - 3) * (n - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = pts[n - 1]!.split(',');
    conn.spDot.setAttribute('cx', last[0]!);
    conn.spDot.setAttribute('cy', last[1]!);
  }
  function pushSample(conn: Conn) {
    conn.hist.push(spSample(conn));
    if (conn.hist.length > HIST_N) conn.hist.shift();
    drawSpark(conn);
  }
  let sparkTimer = 0;
  function sparksRun(on: boolean) {
    window.clearInterval(sparkTimer);
    sparkTimer = 0;
    if (on) sparkTimer = window.setInterval(() => CONNS.forEach(pushSample), 640);
  }
  CONNS.forEach((conn) => {
    seedHist(conn);
    drawSpark(conn);
  });
  function paintConns() {
    CONNS.forEach((conn) => {
      const title = conn.el.querySelector('.mc-route-city');
      const kind = conn.el.querySelector('.mc-route-kind');
      /* the sub-line already says the kind: the title is the brand alone */
      if (title) title.textContent = kind ? conn.brand : `${conn.brand} ${conn.kind}`;
      if (kind) kind.textContent = conn.kind;
      conn.el.setAttribute('aria-label', labelFor(conn));
    });
    if (miDesc) miDesc.textContent = S.description(S.list(CONNS.map((conn) => conn.sr)));
  }
  paintConns();
  if (!forced)
    askVisitorGeo().then((data) => {
      const network = normalizeNetwork(data);
      if (network && !destroyed) {
        applyProviders(pickProviders(GEO, network.country, network.isp, S));
        paintConns();
      }
    });

  const byId = (id: string) => CONNS.find((conn) => conn.id === id) ?? null;
  const active = () => CONNS.filter((conn) => conn.on && !conn.down);
  const comboText = (act: Conn[]) => S.combo(act.length);
  /* ---------- state ---------- */
  let paused = false;
  let dropping = false;
  let visible = true;
  let running = false;
  let built = false;
  let hoverId: string | null = null;
  let stepTimer = 0;
  let dropTimer = 0;
  const supportsMotion = typeof pkLayer.animate === 'function';
  const boost = 1;
  const durFor = (conn: Conn) => 1500 - conn.mbps * 0.9;
  const rateFor = (conn: Conn) => (BASE_MS / durFor(conn)) * boost;
  function labelFor(conn: Conn) {
    const base = S.connAria(conn.sr, conn.mbps);
    if (conn.down) return base + (active().length ? S.connAriaDownCovered : S.connAriaDownAlone);
    /* neutral wording, so the spoken state never disagrees with the visible chip */
    return base + (conn.on ? S.connAriaOn : S.connAriaOff);
  }
  /* one-shot CSS effects are restarted by dropping the class now and putting it back on the next frame */
  let pulseQ: { el: Element; cls: string }[] = [];
  let pulseRaf = 0;
  function pulseFlush() {
    pulseRaf = 0;
    const queue = pulseQ;
    pulseQ = [];
    queue.forEach((item) => item.el.classList.add(item.cls));
  }
  function pulse(el: Element | null | undefined, cls: string) {
    if (reduced.matches || !el) return;
    if (!el.classList.contains(cls)) {
      el.classList.add(cls);
      return;
    }
    el.classList.remove(cls);
    if (pulseQ.some((item) => item.el === el && item.cls === cls)) return;
    pulseQ.push({ el, cls });
    if (!pulseRaf) pulseRaf = requestAnimationFrame(pulseFlush);
  }
  const flip = (el: Element) => pulse(el, 'is-flip');
  function swapText(el: HTMLElement, text: string, hot?: boolean) {
    if (el.textContent === text && (hot === undefined || el.classList.contains('is-failover') === !!hot)) return;
    el.textContent = text;
    if (hot !== undefined) el.classList.toggle('is-failover', !!hot);
    pulse(el, 'is-swap');
  }
  const trunk: Trunk = { g: null, set: null, packets: [], frames: null, parkIdx: null, d: '', parks: [0.22, 0.7], wasOn: null, offT: 0, len: 0, hide: null, live: 2 };
  let wasKey = '';
  /* every figure on screen derives from CONNS in this one pass */
  function syncDerived() {
    const act = active();
    const any = act.length > 0;
    const anyDown = CONNS.some((conn) => conn.down);
    let sum = 0;
    act.forEach((conn) => {
      sum += conn.mbps;
    });
    const fAll = fmtCap(sum);
    const keyNow = `${act.length}|${any ? 1 : 0}`;
    countEl.textContent = String(act.length);
    countW.textContent = S.activeWord(act.length);
    if (wasKey && keyNow !== wasKey) flip(keyEl);
    wasKey = keyNow;
    CONNS.forEach((conn) => {
      const on = conn.on && !conn.down;
      const { down } = conn;
      const changed = on !== conn.wasOn || down !== conn.wasDown;
      if (down !== conn.wasDown && conn.wasDown !== undefined) snap([conn.el, conn.g, conn.pg]);
      conn.el.classList.toggle('is-on', on);
      conn.el.classList.toggle('is-down', down);
      conn.el.classList.toggle('is-off', !on && !down);
      conn.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      conn.el.setAttribute('aria-label', labelFor(conn));
      conn.tagEl.textContent = down ? S.tagDown : on ? S.tagOn : S.tagOff;
      conn.mbEl.textContent = down ? '0' : String(conn.mbps);
      conn.deltaEl.textContent = down ? S.deltaDown : on ? S.deltaOn : S.deltaOff;
      if (conn.g && conn.pg) {
        conn.g.classList.toggle('is-on', on);
        conn.g.classList.toggle('is-down', down);
        conn.g.classList.toggle('is-off', !on && !down);
        conn.pg.classList.toggle('is-on', on);
        conn.pg.classList.toggle('is-down', down);
        if (down !== conn.wasDown) syncStub(conn);
      }
      if (conn.set) conn.set.classList.toggle('is-on', on && !busMode);
      if (changed) {
        /* the sparkline reacts at once; static modes redraw the whole trace */
        if (sparkTimer) pushSample(conn);
        else {
          seedHist(conn);
          drawSpark(conn);
        }
        if (conn.segIn) setPlay(conn, on);
      }
      conn.wasOn = on;
      conn.wasDown = down;
    });
    /* the phone spine detours around a fallen station the moment it falls */
    if (busMode) updateBusTrunk();
    if (trunk.g) {
      trunk.g.classList.toggle('is-on', any);
      trunk.g.classList.toggle('is-off', !any);
    }
    if (trunk.set) {
      trunk.set.classList.toggle('is-on', any);
      if (any !== trunk.wasOn) {
        setPlay(trunk, any);
        trunk.wasOn = any;
      }
    }
    if (ports.npIn) {
      ports.pcOut!.classList.toggle('is-hot', any);
      ports.npIn.classList.toggle('is-hot', any);
      ports.npOut!.classList.toggle('is-hot', any);
      ports.gameIn!.classList.toggle('is-hot', any);
    }
    root.classList.toggle('is-offline', !any);
    /* ONE connection carrying the game IS the no-reserve state, except while the failover payoff is on screen */
    const bad = anyDown && !any;
    const noRes = act.length === 1;
    const telling = anyDown && any && !!resultNote;
    const lone = noRes && !telling;
    if (act.length >= 2) kicked = false;
    const counted = bad || (kicked && noRes);
    root.classList.toggle('is-weak', lone);
    /* the proof card: "uma internet só" is the fastest active link, "Com Multi Internet" every active link combined */
    let pb = 0;
    act.forEach((conn) => {
      if (conn.mbps > pb) pb = conn.mbps;
    });
    const fBase = fmtCap(pb);
    const pctN = pb ? Math.round(((sum - pb) / pb) * 100) : 0;
    pBase.textContent = pb ? fBase.n : '0';
    pBaseU.textContent = pb ? fBase.u : S.mbps;
    pSum.textContent = sum ? fAll.n : '0';
    pSumU.textContent = sum ? fAll.u : S.mbps;
    pPct.textContent = `+${pctN}%`;
    pPct.style.visibility = pctN > 0 ? '' : 'hidden';
    const pScale = sum;
    const wBad = pScale ? Math.round((pb / pScale) * 100) : 0;
    const wGood = pScale ? 100 : 0;
    pFill.style.setProperty('--mc-fill', `${wBad}%`);
    pFill.classList.toggle('is-empty', !wBad);
    pFillGood.style.setProperty('--mc-fill', `${wGood}%`);
    pFillGood.classList.toggle('is-empty', !wGood);
    const dropsText = S.drops(counted ? 1 : 0);
    pDrops.textContent = dropsText;
    pDrops.classList.toggle('is-bad', counted);
    if (proofSr) proofSr.textContent = S.proofSr(pb ? fBase.sr : S.noBandwidth, sum ? fAll.sr : S.noBandwidth, pctN, dropsText);
    /* the backup card and the trunk glow are held for the whole outage */
    const failover = anyDown && any && !lone;
    stage.classList.toggle('is-failover', failover);
    setBackup(!any ? S.backupNone : failover ? S.backupHot : noRes ? S.backupLone : S.backupRest);
    if (srNow) srNow.textContent = bad ? S.srNowOut : !any ? S.srNowNone : S.srNowActive(act.length, TOTAL, fAll.sr, act.length >= 2, dropsText, lone ? 'lone' : anyDown ? 'covered' : 'ok');
    swapText(npSub, !any ? (anyDown ? S.npSubNoReserve : S.npSubNoConnection) : lone ? S.npSubLone : anyDown ? S.npSubTookOver : S.npSubCombines, failover);
    swapText(gameSub, !any ? (anyDown ? S.gameSubDropped : S.gameSubWaiting) : lone ? S.gameSubNoReserve : S.gameSubContinuous);
    sumEl.textContent = any ? fAll.n : '0';
    sumU.textContent = any ? fAll.u : S.mbps;
    sumB.textContent = bad ? S.sumOut : !any ? S.sumNone : lone ? (kicked ? S.sumOneDrop : S.sumNoReserve) : S.sumContinuous;
    sumRow.classList.toggle('is-bad', bad);
    sumRow.classList.toggle('is-weak', lone);
    const names2 = act.length === 2 ? `${nb(act[0]!.name)} + ${nb(act[1]!.name)}` : '';
    sumSub.innerHTML = resultNote || (!any ? S.sumSubNone : act.length === 1 ? alt(kicked ? S.sumSubLoneKicked(nb(act[0]!.name)) : S.sumSubLone(nb(act[0]!.name))) : act.length === 2 ? alt(S.sumSubPair(names2)) : alt(S.sumSubMany(act.length)));
    /* "Simular falha" works with a single connection too: it then plays the honest outcome */
    const canDrop = act.length >= 1 && !dropping && !(reduced.matches && anyDown && !any);
    dropBtn.setAttribute('aria-disabled', canDrop ? 'false' : 'true');
    dropBtn.title = dropping ? S.dropTitleWait : reduced.matches ? (anyDown ? S.dropTitleReconnect : S.dropTitleDefault) : act.length < 2 ? S.dropTitleAlone : S.dropTitleDefault;
    hintText.classList.toggle('is-done', hintGone && act.length >= 2);
    if (running) setRates();
  }
  /* packets of a set that is not in the combo are paused once its fade-out has finished */
  const setPackets = (owner: Conn | Trunk): Packet[] => ('segIn' in owner && owner.segIn ? [...owner.segIn.packets, ...owner.segOut!.packets] : (owner as Trunk).packets);
  function setPlay(owner: Conn | Trunk, on: boolean) {
    const pk = setPackets(owner);
    clearLater(owner.offT ?? 0);
    owner.offT = 0;
    if (on) {
      if (running)
        pk.forEach((packet) => {
          if (packet.anim && packet.anim.playState !== 'running') packet.anim.play();
        });
      return;
    }
    owner.offT = later(() => {
      owner.offT = 0;
      pk.forEach((packet) => packet.anim?.pause());
    }, 460);
  }
  /* the green claim is only made when there is something to fail over to */
  function stabHtml() {
    const act = active();
    if (!act.length) return `<b class="mi-stab is-off">${S.stabNone}</b>`;
    if (act.length === 1) return `<b class="mi-stab is-off">${S.stabLone}</b>`;
    return `<b class="mi-stab">${S.stabOk}</b>`;
  }
  function say(html: string, fade: boolean, raw?: boolean) {
    const full = raw ? html : `${stabHtml()} · ${html}`;
    if (statusText.innerHTML === full) return;
    statusText.innerHTML = full;
    if (fade) pulse(statusText, 'is-swap');
  }
  function live(msg: string) {
    liveEl.textContent = '';
    liveEl.textContent = msg;
  }
  function setBackup(pair: [string, string]) {
    if (backupSub.textContent === pair[1] && backupB.textContent === pair[0]) return;
    backupB.textContent = pair[0];
    backupSub.textContent = pair[1];
    pulse(backupSub, 'is-swap');
  }
  function burstNode() {
    if (reduced.matches) return;
    pulse(npIcon, 'is-burst');
    pulse(sumStrong, 'is-hit');
  }
  /* a card flipping between down and up snaps every colour for one painted frame */
  let snapRaf = 0;
  let snapT = 0;
  let snapEls: Element[] = [];
  function unsnap() {
    snapRaf = 0;
    clearLater(snapT);
    snapT = 0;
    const els = snapEls;
    snapEls = [];
    els.forEach((el) => el.classList.remove('is-snap'));
  }
  function snap(els: (Element | undefined)[]) {
    els.forEach((el) => {
      if (el) {
        el.classList.add('is-snap');
        snapEls.push(el);
      }
    });
    if (snapRaf) return;
    snapRaf = requestAnimationFrame(() => {
      snapRaf = requestAnimationFrame(unsnap);
    });
    snapT = later(unsnap, 200);
  }
  /* ---------- actions ---------- */
  function turnOn(id: string, burst: boolean) {
    const conn = byId(id);
    if (!conn) return;
    conn.on = true;
    conn.down = false;
    resultNote = null;
    syncDerived();
    if (burst) pulse(conn.el, 'is-burst');
  }
  function turnOff(id: string) {
    const conn = byId(id);
    if (!conn) return;
    conn.on = false;
    conn.down = false;
    resultNote = null;
    conn.el.classList.remove('is-burst');
    syncDerived();
  }
  function dropConn(id: string) {
    const conn = byId(id);
    if (!conn || !conn.on || conn.down) return null;
    conn.down = true;
    const rest = active();
    if (!rest.length) kicked = true;
    resultNote = `${S.noteFailed(nb(conn.name))}${!rest.length ? alt(S.noteNoReserve) : rest.length === 1 ? alt(S.noteOneTook(nb(rest[0]!.name))) : rest.length > 2 ? S.noteManyTook(rest.length) : alt(S.notePairHold(nb(rest[0]!.name), nb(rest[1]!.name)))}`;
    syncDerived();
    pulse(conn.el, 'is-shake');
    if (!active().length) {
      /* the honest single-internet outcome: no reserve, no failover */
      say(S.sayFailedAlone(nb(conn.name)), true, true);
      return conn;
    }
    burstNode();
    say(S.sayFailedCovered(nb(conn.name)), true, true);
    return conn;
  }
  /* the spoken version of a drop / recovery (user-triggered beats only) */
  function dropSr(conn: Conn) {
    const others = active();
    if (!others.length) return S.srFailedAlone(conn.sr);
    return S.srFailedCovered(conn.sr, others.length > 2 ? S.srViaMany(others.length) : S.list(others.map((other) => S.srVia(other.sr))));
  }
  function recoverSr(conn: Conn) {
    const act = active();
    return act.length >= 2 ? S.srRecoveredMany(conn.sr, comboText(act)) : kicked ? S.srRecoveredKicked(conn.sr) : S.srRecoveredLone(conn.sr);
  }
  function recoverConn(id: string) {
    const conn = byId(id);
    if (!conn || !conn.down) return;
    const wasLone = !active().length;
    conn.down = false;
    conn.on = true;
    const actR = active();
    resultNote = wasLone ? alt(S.noteBackLone(nb(conn.name))) : alt(S.noteBackMany(nb(conn.name), actR.length));
    syncDerived();
    pulse(conn.el, 'is-burst');
    if (wasLone) say(S.sayBackLone(nb(conn.name)), true);
    else say(S.sayBackMany(nb(conn.name)), true);
  }
  function setState(map: Record<string, boolean>) {
    CONNS.forEach((conn) => {
      conn.on = !!map[conn.id];
      conn.down = false;
      conn.el.classList.remove('is-burst');
    });
    resultNote = null;
    kicked = false;
    syncDerived();
  }
  /* ---------- auto-demo (looping script) ---------- */
  let lastDrop: string | null = null;
  const nextReserve = () => CONNS.find((conn) => !conn.on && !conn.down) ?? null;
  function pickDrop() {
    const act = active();
    if (act.length < 2) return null;
    const sorted = act.slice().sort((first, second) => second.mbps - first.mbps);
    return sorted[0]!.id === lastDrop && sorted.length > 1 ? sorted[1]! : sorted[0]!;
  }
  function stepReset() {
    const extras = CONNS.filter((conn) => (conn.on || conn.down) && conn.id !== 'fibra' && conn.id !== 'wifi');
    setState({ fibra: true, wifi: true });
    if (extras.length) say(S.sayResetExtras(S.list(extras.map((conn) => nb(conn.name))), extras.length), true);
    else say(S.sayReset, true);
  }
  function stepAdd(second: boolean) {
    const conn = nextReserve();
    if (!conn) {
      say(S.sayAllActive, true);
      return;
    }
    turnOn(conn.id, true);
    say(S.sayAdded(nb(conn.name), second), true);
  }
  function stepDrop() {
    const target = pickDrop();
    if (!target) {
      if (!touched) stepAdd(false);
      return;
    }
    lastDrop = target.id;
    dropConn(target.id);
  }
  function stepRecover() {
    const fallen = CONNS.find((conn) => conn.down);
    if (fallen) recoverConn(fallen.id);
  }
  /* scripted story (untouched visitor): pair → +1 → failure → back → +1 → failure → back → narrated restart */
  const STEPS = [
    { wait: 3600, run: stepReset },
    { wait: 3600, run: () => stepAdd(false) },
    { wait: 3900, run: stepDrop },
    { wait: 4200, run: stepRecover },
    { wait: 3600, run: () => stepAdd(true) },
    { wait: 3900, run: stepDrop },
    { wait: 4600, run: stepRecover },
  ];
  /* once the visitor has touched a card the scripted add/remove beats stop for good; the failover beat keeps playing against their own set */
  const STEPS_USER = [
    {
      wait: 4600,
      run: () => {
        if (active().length >= 2) stepDrop();
      },
    },
    { wait: 9500, run: stepRecover },
  ];
  let touched = false;
  let stepIdx = 1;
  let lastMove = 0;
  function engaged() {
    let keyboard = false;
    try {
      keyboard = !!list.querySelector(':focus-visible');
    } catch {
      keyboard = list.contains(document.activeElement);
    }
    return keyboard || (!!hoverId && Date.now() - lastMove < 6000);
  }
  const canRun = () => built && visible && !document.hidden && !reduced.matches && !paused && !dropping && !engaged();
  /* dormant = nothing to wait for: the timer chain stops dead and is re-armed by the observers / the pause button */
  const dormant = () => !visible || document.hidden || paused || reduced.matches;
  function schedule(ms: number) {
    clearLater(stepTimer);
    stepTimer = later(tickStep, ms);
  }
  function tickStep() {
    stepTimer = 0;
    if (!canRun()) {
      if (!dormant()) schedule(700);
      return;
    }
    const steps = touched ? STEPS_USER : STEPS;
    if (touched && stepIdx >= steps.length) stepIdx = 0;
    /* a fallen connection is always brought back before the next beat */
    if (touched && stepIdx === 0 && CONNS.some((conn) => conn.down)) stepIdx = 1;
    const step = steps[stepIdx]!;
    step.run();
    stepIdx = (stepIdx + 1) % steps.length;
    schedule(step.wait);
  }
  function takeOver() {
    touched = true;
    stepIdx = 0;
    clearLater(stepTimer);
    stepTimer = 0;
    if (!dormant()) schedule(USER_IDLE);
  }
  /* ---------- user interaction ---------- */
  let hintGone = false;
  function dismissHint() {
    if (hintGone) return;
    hintGone = true;
    if (active().length >= 2) {
      hintText.classList.add('is-done');
      flip(hintText);
    }
  }
  const loneMsg = (conn: Conn) => (kicked ? S.loneKicked(nb(conn.name)) : S.lone(nb(conn.name)));
  listen(list, 'click', (event: MouseEvent) => {
    const button = event.target instanceof Element ? event.target.closest('[data-conn]') : null;
    if (!button) return;
    const conn = byId(button.getAttribute('data-conn') || '');
    if (!conn) return;
    takeOver();
    dismissHint();
    if (conn.down) {
      recoverConn(conn.id);
      live(recoverSr(conn));
      if (reduced.matches && !CONNS.some((other) => other.down)) dropLbl.textContent = S.dropLabel;
    } else if (conn.on) {
      if (active().length === 1) {
        /* the last connection stays on */
        pulse(conn.el, 'is-nudge');
        say(S.sayKeepOne, true);
        live(S.liveKeepOne);
        return;
      }
      turnOff(conn.id);
      const act = active();
      if (act.length === 1) say(loneMsg(act[0]!), true);
      else say(S.sayToReserve(nb(conn.name)), true);
      live(S.liveToReserve(conn.sr, act.length === 1 ? S.liveOnlyOne(act[0]!.sr) : `${comboText(act)}.`));
    } else {
      turnOn(conn.id, true);
      const now = active();
      if (now.length === 1) say(loneMsg(conn), true);
      else say(S.sayJoined(nb(conn.name)), true);
      live(S.liveJoined(conn.sr, now.length === 1 ? S.liveOnlyOneNoReserve : `${comboText(now)}.`));
    }
    if (reduced.matches || !supportsMotion) drawStatic();
  });
  listen(list, 'pointerover', (event: PointerEvent) => {
    const button = event.target instanceof Element ? event.target.closest('[data-conn]') : null;
    if (!button || event.pointerType === 'touch') return;
    hoverId = button.getAttribute('data-conn');
    lastMove = Date.now();
  });
  listen(list, 'pointermove', (event: PointerEvent) => {
    if (event.pointerType !== 'touch') lastMove = Date.now();
  });
  listen(list, 'pointerout', (event: PointerEvent) => {
    const button = event.target instanceof Element ? event.target.closest('[data-conn]') : null;
    if (!button || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    hoverId = null;
  });
  const fastest = (act: Conn[]) => act.slice().sort((first, second) => second.mbps - first.mbps)[0]!;
  listen(dropBtn, 'click', () => {
    if (dropBtn.getAttribute('aria-disabled') === 'true') {
      /* a dimmed control still answers a tap */
      if (!dropping) say(S.sayActivateToDrop, true);
      live(dropBtn.title);
      return;
    }
    const act = active();
    if (reduced.matches) {
      /* no motion: the button swaps the two stills */
      takeOver();
      dismissHint();
      const downC = CONNS.find((conn) => conn.down) ?? null;
      if (downC) {
        recoverConn(downC.id);
        dropLbl.textContent = S.dropLabel;
        live(recoverSr(downC));
      } else if (act.length) {
        const target = fastest(act);
        dropConn(target.id);
        dropLbl.textContent = S.reconnectLabel;
        live(dropSr(target));
      }
      drawStatic();
      return;
    }
    if (!act.length) return;
    takeOver();
    dismissHint();
    const target = fastest(act);
    dropping = true;
    dropBtn.classList.add('is-busy');
    dropBtn.setAttribute('aria-busy', 'true');
    dropLbl.textContent = S.reconnectingLabel;
    dropConn(target.id);
    bridge.classList.add('is-hot');
    live(dropSr(target));
    if (!supportsMotion) drawStatic();
    clearLater(dropTimer);
    dropTimer = later(() => {
      dropping = false;
      recoverConn(target.id);
      live(recoverSr(target));
      dropBtn.classList.remove('is-busy');
      dropBtn.setAttribute('aria-busy', 'false');
      dropLbl.textContent = S.dropLabel;
      syncDerived();
      if (!supportsMotion) drawStatic();
      takeOver();
    }, 3400);
  });
  listen(pauseBtn, 'click', () => {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    pauseBtn.setAttribute('aria-label', paused ? S.resumeAria : S.pauseAria);
    const actP = active();
    const anyDownP = CONNS.some((conn) => conn.down);
    const stateP = anyDownP ? S.pausedStateDown(actP.length ? (actP.length === 1 ? S.pausedHoldingOne(nb(actP[0]!.name)) : S.pausedHoldingMany(actP.length)) : S.pausedNoReserve) : actP.length >= 2 ? S.pausedCombined(comboText(actP)) : actP.length === 1 ? S.pausedOnlyOne(nb(actP[0]!.name)) : S.pausedNone;
    if (paused) {
      stop();
      clearLater(stepTimer);
      stepTimer = 0;
      say(S.sayPaused(stateP), false);
    } else {
      syncRun();
      say(S.sayResumed(stateP, actP.length), true);
    }
    live(paused ? S.livePaused : S.liveResumed);
  });
  listen(root, 'keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && document.activeElement instanceof HTMLElement && list.contains(document.activeElement)) document.activeElement.blur();
  });
  /* ---------- geometry ---------- */
  const ports: { pcOut?: SVGCircleElement; npIn?: SVGCircleElement; npOut?: SVGCircleElement; gameIn?: SVGCircleElement } = {};
  let busL: SVGPathElement | null = null;
  let busR: SVGPathElement | null = null;
  let busMode = false;
  let busGeo: { x: number; pcB: number; npT: number; npB: number; gmT: number } | null = null;
  let busKey = '';
  const tileMask: { el: SVGMaskElement | null; all: SVGRectElement | null; rects: SVGRectElement[] } = { el: null, all: null, rects: [] };
  const svgEl = <K extends keyof SVGElementTagNameMap>(tag: K) => document.createElementNS(NS, tag) as SVGElementTagNameMap[K];
  function setTileMask(w: number, h: number, boxes: Box[]) {
    const m = tileMask;
    if (!m.el || !m.all) return;
    m.el.setAttribute('x', '0');
    m.el.setAttribute('y', '0');
    m.el.setAttribute('width', String(f(w)));
    m.el.setAttribute('height', String(f(h)));
    m.all.setAttribute('width', String(f(w)));
    m.all.setAttribute('height', String(f(h)));
    m.rects.forEach((rect, i) => {
      const bx = boxes[i];
      if (bx) {
        rect.setAttribute('x', String(f(bx.l)));
        rect.setAttribute('y', String(f(bx.t)));
        rect.setAttribute('width', String(f(bx.w)));
        rect.setAttribute('height', String(f(bx.h)));
      } else {
        rect.setAttribute('width', '0');
        rect.setAttribute('height', '0');
      }
    });
  }
  function makeSet(cls: string) {
    const set = document.createElement('div');
    set.className = `pk-set${cls ? ` ${cls}` : ''}`;
    pkLayer.appendChild(set);
    return set;
  }
  function makePackets(set: HTMLDivElement, n: number) {
    const packets: Packet[] = [];
    for (let i = 0; i < n; i++) {
      const el = document.createElement('div');
      el.className = 'pk';
      set.appendChild(el);
      packets.push({ el, phase: (i + 0.15) / n, anim: null });
    }
    return packets;
  }
  const seg = (parks: number[]): Segment => ({ d: '', parks, packets: [], frames: null, parkIdx: null });
  function mkPath(cls: string, parent: Element) {
    const path = svgEl('path');
    if (cls) path.setAttribute('class', cls);
    parent.appendChild(path);
    return path;
  }
  function buildStage() {
    if (built || destroyed) return;
    built = true;
    /* every wire layer is drawn through one mask that cuts out the node tiles (and, on the phone, the station tiles) */
    const defs = svgEl('defs');
    tileMask.el = svgEl('mask');
    tileMask.el.setAttribute('id', 'mi-tm');
    tileMask.el.setAttribute('class', 'w-tilemask');
    tileMask.el.setAttribute('maskUnits', 'userSpaceOnUse');
    tileMask.all = svgEl('rect');
    tileMask.all.setAttribute('class', 'w-maskall');
    tileMask.el.appendChild(tileMask.all);
    for (let mi = 0; mi < 9; mi++) {
      const mr = svgEl('rect');
      mr.setAttribute('width', '0');
      mr.setAttribute('height', '0');
      tileMask.el.appendChild(mr);
      tileMask.rects.push(mr);
    }
    defs.appendChild(tileMask.el);
    svg.appendChild(defs);
    const wires = svgEl('g');
    wires.setAttribute('mask', 'url(#mi-tm)');
    svg.appendChild(wires);
    const portLayer = svgEl('g');
    const tg = svgEl('g');
    tg.setAttribute('class', 'w-trunkg');
    trunk.base = mkPath('w-base', tg);
    trunk.glow = mkPath('w-glow', tg);
    const thw = svgEl('g');
    thw.setAttribute('class', 'w-hotwrap');
    trunk.hot = mkPath('w-hot', thw);
    tg.appendChild(thw);
    wires.appendChild(tg);
    trunk.g = tg;
    busL = mkPath('w-base w-bus', wires);
    busR = mkPath('w-base w-bus', wires);
    CONNS.forEach((conn) => {
      const g = svgEl('g');
      g.setAttribute('data-wire', conn.id);
      const dw = svgEl('g');
      dw.setAttribute('class', 'w-downwrap');
      conn.baseIn = mkPath('w-base w-in', dw);
      conn.baseOut = mkPath('w-base w-out', dw);
      conn.baseOut.setAttribute('pathLength', '100');
      g.appendChild(dw);
      conn.cut = svgEl('g');
      conn.cut.setAttribute('class', 'w-cut');
      const ring = svgEl('circle');
      ring.setAttribute('r', '4.5');
      const cross = svgEl('path');
      cross.setAttribute('d', 'M-1.9,-1.9 L1.9,1.9 M1.9,-1.9 L-1.9,1.9');
      conn.cut.appendChild(ring);
      conn.cut.appendChild(cross);
      dw.appendChild(conn.cut);
      conn.glowIn = mkPath('w-glow', g);
      conn.glowOut = mkPath('w-glow', g);
      const hw = svgEl('g');
      hw.setAttribute('class', 'w-hotwrap');
      conn.hotIn = mkPath('w-hot', hw);
      conn.hotOut = mkPath('w-hot', hw);
      g.appendChild(hw);
      wires.appendChild(g);
      conn.g = g;
      /* the card's two jack dots live outside the mask, in a twin group that mirrors the wire group's state */
      conn.pg = svgEl('g');
      conn.portIn = svgEl('circle');
      conn.portIn.setAttribute('r', '2.75');
      conn.portIn.setAttribute('class', 'w-rport w-rp-in');
      conn.pg.appendChild(conn.portIn);
      conn.portOut = svgEl('circle');
      conn.portOut.setAttribute('r', '2.75');
      conn.portOut.setAttribute('class', 'w-rport w-rp-out');
      conn.pg.appendChild(conn.portOut);
      portLayer.appendChild(conn.pg);
      conn.set = makeSet('');
      conn.segIn = seg([0.38, 0.8]);
      conn.segIn.packets = makePackets(conn.set, 2);
      conn.segOut = seg([0.3, 0.6, 0.9]);
      conn.segOut.packets = makePackets(conn.set, 3);
    });
    trunk.set = makeSet('is-trunk');
    trunk.packets = makePackets(trunk.set, TRUNK_PHASES_BUS.length);
    (['pcOut', 'npIn', 'npOut', 'gameIn'] as const).forEach((key) => {
      const port = svgEl('circle');
      port.setAttribute('r', '4');
      port.setAttribute('class', 'w-port');
      portLayer.appendChild(port);
      ports[key] = port;
    });
    svg.appendChild(portLayer);
    syncDerived();
    layout(true);
  }
  function box(rect: DOMRect, s: DOMRect): Box {
    return { l: rect.left - s.left, t: rect.top - s.top, r: rect.right - s.left, b: rect.bottom - s.top, w: rect.width, h: rect.height, cx: rect.left - s.left + rect.width / 2, cy: rect.top - s.top + rect.height / 2 };
  }
  const rel = (el: Element, s: DOMRect) => box(el.getBoundingClientRect(), s);
  /* card → NoPing socket: early bend, flat arrival */
  function cubR(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const c1 = Math.min(Math.max(dx * BEND_A, 10), dx * 0.45);
    return `C${f(x1 + c1)},${f(y1)} ${f(x1 + Math.max(dx * BEND_B, c1 + 6))},${f(y2)} ${f(x2)},${f(y2)}`;
  }
  /* PC socket → card jack: flat departure, late bend */
  function cubL(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    return `C${f(x2 - dx * BEND_B)},${f(y1)} ${f(x2 - dx * BEND_A)},${f(y2)} ${f(x2)},${f(y2)}`;
  }
  let lastKey = '';
  const setD = (path: SVGPathElement | undefined, d: string) => path?.setAttribute('d', d);
  /* the severed stub: red dashes up to `frac` of the card→NoPing stroke, then nothing */
  function cutDash(frac: number) {
    const out: number[] = [];
    let pos = 0;
    const end = frac * 100;
    while (pos + 3 <= end) {
      out.push(3, 3.5);
      pos += 6.5;
    }
    out.push(0, 100);
    return out.join(' ');
  }
  /* path geometry in plain JS (absolute M / L / C only): no getTotalLength, no forced reflow */
  function samplePath(d: string) {
    const tokens = d.match(/[MLC]|-?\d*\.?\d+/g) || [];
    let i = 0;
    const pts: { x: number; y: number; s: number }[] = [];
    let total = 0;
    let cur: { x: number; y: number } | null = null;
    let cmd = '';
    const num = () => parseFloat(tokens[i++]!);
    const push = (x: number, y: number, jump: boolean) => {
      if (cur && !jump) total += Math.hypot(x - cur.x, y - cur.y);
      cur = { x, y };
      pts.push({ x, y, s: total });
    };
    while (i < tokens.length) {
      const tok = tokens[i];
      if (tok === 'M' || tok === 'L' || tok === 'C') {
        cmd = tok;
        i++;
        continue;
      }
      if (cmd === 'M') {
        push(num(), num(), true);
        cmd = 'L';
      } else if (cmd === 'L') push(num(), num(), false);
      else if (cmd === 'C' && cur) {
        const { x: x0, y: y0 } = cur as { x: number; y: number };
        const x1 = num();
        const y1 = num();
        const x2 = num();
        const y2 = num();
        const x3 = num();
        const y3 = num();
        for (let k = 1; k <= 24; k++) {
          const u = k / 24;
          const v = 1 - u;
          push(v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x3, v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y3, false);
        }
      } else i++;
    }
    return { pts, len: total };
  }
  function pointAt(poly: { pts: { x: number; y: number; s: number }[]; len: number }, s: number) {
    const { pts } = poly;
    let lo = 0;
    let hi = pts.length - 1;
    if (!pts.length) return { x: 0, y: 0 };
    if (s <= 0) return pts[0]!;
    if (s >= poly.len) return pts[hi]!;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (pts[mid]!.s <= s) lo = mid;
      else hi = mid;
    }
    const a = pts[lo]!;
    const b = pts[hi]!;
    const span = b.s - a.s;
    const r = span > 0 ? (s - a.s) / span : 0;
    return { x: a.x + (b.x - a.x) * r, y: a.y + (b.y - a.y) * r };
  }
  function placeCut(conn: Conn) {
    conn.cut!.setAttribute('transform', conn.cutXY!);
    conn.baseOut!.style.setProperty('--mc-cut-dash', '25 20 25 100');
    conn.baseOut!.style.strokeDashoffset = '-25';
  }
  /* the out stroke is the live curve while the card is up and the straight severed stub while it is down */
  function syncStub(conn: Conn) {
    if (busMode) {
      placeStation(conn);
      return;
    }
    if (!conn.stubD) return;
    conn.baseOut!.setAttribute('d', conn.down ? conn.stubD : conn.segOut!.d);
  }
  /* phone station: the spine runs unbroken in its own gutter and every card hangs off it by one short branch */
  function placeStation(conn: Conn) {
    const g = conn.busR;
    if (!g) return;
    const { x } = g;
    const rc = g.card;
    const { cy } = rc;
    const jx = rc.l - JACK_D;
    if (conn.down) {
      conn.baseIn!.setAttribute('d', '');
      conn.baseIn!.style.strokeDasharray = '';
      setD(conn.glowIn, '');
      setD(conn.hotIn, '');
      conn.baseOut!.setAttribute('d', `M${f(jx)},${f(cy)} L${f(jx - 9)},${f(cy)}`);
      conn.baseOut!.style.setProperty('--mc-cut-dash', '25 20 25 100');
      conn.baseOut!.style.strokeDashoffset = '-25';
      conn.cut!.setAttribute('transform', `translate(${f(jx - 13)},${f(cy)})`);
      conn.cut!.style.display = '';
      conn.portIn!.setAttribute('cx', String(f(jx)));
      conn.portIn!.setAttribute('cy', String(f(cy)));
      conn.portOut!.style.display = 'none';
      return;
    }
    const br = `M${f(x)},${f(cy)} L${f(jx)},${f(cy)}`;
    conn.baseIn!.setAttribute('d', br);
    conn.baseIn!.style.strokeDasharray = '';
    setD(conn.glowIn, br);
    setD(conn.hotIn, br);
    conn.baseOut!.setAttribute('d', '');
    conn.baseOut!.style.setProperty('--mc-cut-dash', '30 35');
    conn.baseOut!.style.strokeDashoffset = '0';
    conn.cut!.style.display = 'none';
    conn.portIn!.setAttribute('cx', String(f(x)));
    conn.portIn!.setAttribute('cy', String(f(cy)));
    conn.portOut!.style.display = '';
    conn.portOut!.setAttribute('cx', String(f(jx)));
    conn.portOut!.setAttribute('cy', String(f(cy)));
  }
  /* phone bus: ONE unbroken vertical line in the gutter — Seu PC → NoPing → servidor */
  function busTrunk(g: { x: number; pcB: number; npT: number; npB: number; gmT: number }) {
    const { x } = g;
    const d = `M${f(x)},${f(g.pcB + 1)} L${f(x)},${f(g.npT - 1)} M${f(x)},${f(g.npB + 1)} L${f(x)},${f(g.gmT - 1)}`;
    return { d, pk: `M${f(x)},${f(g.pcB + 1)} L${f(x)},${f(g.gmT - 1)}` };
  }
  function updateBusTrunk() {
    if (!busGeo) return;
    const key = CONNS.map((conn) => (conn.down ? 1 : 0)).join('');
    if (key === busKey) return;
    busKey = key;
    const res = busTrunk(busGeo);
    setD(trunk.base, res.d);
    setD(trunk.glow, res.d);
    setD(trunk.hot, res.d);
    trunk.d = res.pk;
    if (trunk.frames) setPath(trunk);
  }
  const shiftBox = (b: Box, dy: number): Box => ({ l: b.l, t: b.t + dy, r: b.r, b: b.b + dy, w: b.w, h: b.h, cx: b.cx, cy: b.cy + dy });
  const nodeDy = new WeakMap<HTMLElement, number>();
  function layout(force: boolean) {
    if (!built || destroyed) return;
    const s = svg.getBoundingClientRect();
    if (!s.width || !s.height) return;
    const mobile = isMobile();
    /* every read first (one layout), then every write */
    const rects = CONNS.map((conn) => rel(conn.el, s));
    const tiles = mobile ? CONNS.map((conn) => rel(conn.ccEl, s)) : null;
    const nodes = [pcNode, npNode, gameNode];
    const icons = [pcIcon, npIcon, gameIcon];
    const nodeR = nodes.map((node) => rel(node, s));
    const iconR = icons.map((icon) => rel(icon, s));
    const cc = (rects[0]!.t + rects[rects.length - 1]!.b) / 2;
    /* all three node groups are centred on the connection-card stack: one horizontal axis for the tiles */
    nodes.forEach((node, ni) => {
      const cur = nodeDy.get(node) || 0;
      let dy = 0;
      if (!mobile) dy = cc - ((nodeR[ni]!.t + nodeR[ni]!.b) / 2 - cur);
      if (Math.abs(dy - cur) > 0.5) {
        node.style.setProperty('--mc-dy', `${f(dy)}px`);
        nodeDy.set(node, dy);
        iconR[ni] = shiftBox(iconR[ni]!, dy - cur);
      }
    });
    const pcR = iconR[0]!;
    const npR = iconR[1]!;
    const gmR = iconR[2]!;
    const key = [Math.round(s.width), Math.round(s.height), mobile ? 1 : 0, Math.round(rects[0]!.t), Math.round(rects[rects.length - 1]!.b), Math.round(pcR.t), Math.round(pcR.l), Math.round(npR.t), Math.round(npR.l), Math.round(gmR.t), Math.round(gmR.l), Math.round(rects[0]!.l)].join('|');
    if (!force && key === lastKey) return;
    lastKey = key;
    if (!pcR.w || !npR.w || !gmR.w || rects.some((rc) => !rc.w || !rc.h)) return;
    svg.setAttribute('viewBox', `0 0 ${s.width} ${s.height}`);
    setTileMask(s.width, s.height, [pcR, npR, gmR, ...(mobile && tiles ? tiles : [])]);
    let d: string;
    let dPk: string;
    const modeChanged = busMode !== mobile;
    busMode = mobile;
    stage.classList.toggle('is-bus', mobile);
    trunk.live = mobile ? TRUNK_PHASES_BUS.length : TRUNK_PHASES_DESK.length;
    trunk.packets.forEach((tp, i) => {
      tp.phase = (mobile ? TRUNK_PHASES_BUS : TRUNK_PHASES_DESK)[i] || 0;
      tp.el.hidden = i >= trunk.live;
      /* a packet that is not shown in this mode has no animation at all */
      if ((modeChanged || tp.el.hidden) && tp.anim) {
        tp.anim.cancel();
        tp.anim = null;
      }
    });
    if (!mobile) {
      /* every stroke docks INTO a port dot that sits just outside the border it arrives at */
      const a = { x: pcR.r + PORT_D, y: pcR.cy };
      const b = { x: npR.l - PORT_D, y: npR.cy };
      CONNS.forEach((conn, i) => {
        const r = rects[i]!;
        const dIn = `M${f(a.x)},${f(a.y)} ${cubL(a.x, a.y, r.l - JACK_D, r.cy)}`;
        const dOut = `M${f(r.r + JACK_D)},${f(r.cy)} ${cubR(r.r + JACK_D, r.cy, b.x, b.y)}`;
        setD(conn.baseIn, dIn);
        setD(conn.glowIn, dIn);
        setD(conn.hotIn, dIn);
        setD(conn.glowOut, dOut);
        setD(conn.hotOut, dOut);
        conn.portIn!.setAttribute('cx', String(f(r.l - JACK_D)));
        conn.portIn!.setAttribute('cy', String(f(r.cy)));
        conn.portOut!.setAttribute('cx', String(f(r.r + JACK_D)));
        conn.portOut!.setAttribute('cy', String(f(r.cy)));
        /* a fallen card's severed stub is a straight run on the card's own centreline */
        conn.stubD = `M${f(r.r + JACK_D)},${f(r.cy)} L${f(r.r + JACK_D + 12)},${f(r.cy)}`;
        conn.cutXY = `translate(${f(r.r + JACK_D + 17.5)},${f(r.cy)})`;
        setD(conn.baseOut, conn.down ? conn.stubD : dOut);
        conn.segIn!.d = dIn;
        conn.segOut!.d = dOut;
        conn.baseIn!.style.strokeDasharray = '';
        conn.cut!.style.display = '';
        conn.portOut!.style.display = '';
        conn.busR = null;
      });
      busL?.setAttribute('d', '');
      busR?.setAttribute('d', '');
      /* NoPing → server: the same curve vocabulary as the fans on either side (a gentle rise) */
      const t1 = { x: npR.r + PORT_D, y: npR.cy };
      const t2 = { x: gmR.l - PORT_D, y: gmR.cy };
      const tdx = t2.x - t1.x;
      const rise = Math.min(12, tdx * 0.14);
      d = `M${f(t1.x)},${f(t1.y)} C${f(t1.x + tdx * 0.34)},${f(t1.y - rise)} ${f(t2.x - tdx * 0.34)},${f(t2.y - rise)} ${f(t2.x)},${f(t2.y)}`;
      dPk = d;
      const dock: [SVGCircleElement | undefined, number, number][] = [
        [ports.pcOut, pcR.r + PORT_D, pcR.cy],
        [ports.npIn, npR.l - PORT_D, npR.cy],
        [ports.npOut, npR.r + PORT_D, npR.cy],
        [ports.gameIn, gmR.l - PORT_D, gmR.cy],
      ];
      dock.forEach(([port, x, y]) => {
        port?.setAttribute('cx', String(f(x)));
        port?.setAttribute('cy', String(f(y)));
      });
      trunk.hide = null;
      busGeo = null;
      busKey = '';
    } else {
      const x = pcR.cx;
      busGeo = { x, pcB: pcR.b, npT: npR.t, npB: npR.b, gmT: gmR.t };
      busKey = CONNS.map((conn) => (conn.down ? 1 : 0)).join('');
      const bus = busTrunk(busGeo);
      d = bus.d;
      dPk = bus.pk;
      trunk.hide = [...tiles!, npR].map((tile) => ({ l: tile.l - 1, t: tile.t - 1, r: tile.r + 1, b: tile.b + 1 }));
      CONNS.forEach((conn, i) => {
        conn.busR = { x, tile: tiles![i]!, card: rects[i]! };
        setD(conn.glowIn, '');
        setD(conn.hotIn, '');
        setD(conn.glowOut, '');
        setD(conn.hotOut, '');
        conn.segIn!.d = '';
        conn.segOut!.d = '';
        placeStation(conn);
      });
      busL?.setAttribute('d', '');
      busR?.setAttribute('d', '');
      const dock: [SVGCircleElement | undefined, number][] = [
        [ports.pcOut, pcR.b + PORT_D],
        [ports.npIn, npR.t - PORT_D],
        [ports.npOut, npR.b + PORT_D],
        [ports.gameIn, gmR.t - PORT_D],
      ];
      dock.forEach(([port, y]) => {
        port?.setAttribute('cx', String(f(x)));
        port?.setAttribute('cy', String(f(y)));
      });
    }
    setD(trunk.base, d);
    setD(trunk.glow, d);
    setD(trunk.hot, d);
    trunk.d = dPk;
    svg.style.setProperty('--mc-cut-dash', cutDash(0.42));
    if (!mobile) CONNS.forEach(placeCut);
    if (modeChanged) {
      /* a mode switch retires the per-connection packet animations and re-derives which sets are shown */
      CONNS.forEach((conn) => {
        if (!conn.segIn) return;
        setPackets(conn).forEach((packet) => {
          if (packet.anim) {
            packet.anim.cancel();
            packet.anim = null;
          }
          packet.el.style.transform = '';
        });
        conn.segIn.frames = null;
        conn.segOut!.frames = null;
      });
      syncDerived();
    }
    stage.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
  }
  /* ---------- packets (WAAPI along the wires) ---------- */
  function allSegs(): Segment[] {
    const out: Segment[] = [];
    CONNS.forEach((conn) => {
      if (conn.segIn) out.push(conn.segIn, conn.segOut!);
    });
    out.push(trunk);
    return out;
  }
  const inRects = (p: { x: number; y: number }, rects: { l: number; t: number; r: number; b: number }[]) => rects.some((rect) => p.x >= rect.l && p.x <= rect.r && p.y >= rect.t && p.y <= rect.b);
  function sampleFrames(d: string, parks: number[], hide: { l: number; t: number; r: number; b: number }[] | null) {
    const poly = samplePath(d);
    const { len } = poly;
    if (!len) return null;
    const n = Math.max(24, Math.min(200, Math.round(len / 6)));
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i <= n; i++) pts.push(pointAt(poly, (len * i) / n));
    const frames: Keyframe[] = [];
    let prevA = 0;
    const vis: boolean[] = [];
    for (let i = 0; i <= n; i++) {
      const p0 = pts[Math.max(0, i - 1)]!;
      const p1 = pts[Math.min(n, i + 1)]!;
      let ang = (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI;
      if (i) {
        while (ang - prevA > 180) ang -= 360;
        while (ang - prevA < -180) ang += 360;
      }
      prevA = ang;
      const frame: Keyframe = { offset: i / n, transform: `translate(${f(pts[i]!.x)}px,${f(pts[i]!.y)}px) rotate(${f(ang)}deg)` };
      const shown = !hide || !inRects(pts[i]!, hide);
      if (hide) frame.opacity = shown ? 1 : 0;
      vis.push(shown);
      frames.push(frame);
    }
    const parkIdx = parks.map((park) => {
      let idx = Math.max(0, Math.min(n, Math.round(park * n)));
      let step = 0;
      /* a parked (static) packet always rests on a visible stretch */
      while (!vis[idx] && step < n) {
        step++;
        idx = Math.min(n, idx + 1);
      }
      return idx;
    });
    return { frames, parkIdx, len };
  }
  function setPath(mover: Segment) {
    if (!mover.d) return;
    const res = sampleFrames(mover.d, mover.parks, mover.hide || null);
    if (!res) return;
    mover.frames = res.frames;
    mover.parkIdx = res.parkIdx;
    mover.len = res.len;
    mover.packets.forEach((packet, i) => {
      if (packet.el.hidden) return;
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect).setKeyframes(res.frames);
        } catch {
          const t = packet.anim.currentTime;
          packet.anim.cancel();
          packet.anim = null;
          makeAnim(packet, res.frames);
          if (packet.anim && t != null) (packet.anim as Animation).currentTime = t;
        }
      } else if (!running) {
        packet.el.style.transform = String(res.frames[res.parkIdx[i % res.parkIdx.length]!]!.transform);
      }
    });
  }
  function makeAnim(packet: Packet, frames: Keyframe[]) {
    packet.anim = packet.el.animate(frames, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS });
  }
  let motionArmed = false;
  let motionQueue: Segment[] = [];
  let motionRaf = 0;
  function scheduleMotion() {
    if (!built) return;
    motionQueue = allSegs();
    cancelAnimationFrame(motionRaf);
    motionRaf = requestAnimationFrame(motionStep);
  }
  function motionStep() {
    if (destroyed) return;
    const obj = motionQueue.shift();
    if (obj) setPath(obj);
    if (motionQueue.length) {
      motionRaf = requestAnimationFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
  }
  function armMotion() {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage.classList.contains('is-ready')) scheduleMotion();
  }
  function ensureAnims(mover: Segment) {
    if (!mover.frames) return;
    mover.packets.forEach((packet) => {
      if (packet.anim || packet.el.hidden) return;
      packet.el.style.transform = '';
      makeAnim(packet, mover.frames!);
    });
  }
  function setRates() {
    CONNS.forEach((conn) => {
      if (!conn.segIn) return;
      const rate = rateFor(conn);
      setPackets(conn).forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rate;
      });
    });
    /* the trunk moves at a steady ~170 px/s whatever its length */
    const tr = trunk.len ? Math.max(0.5, Math.min(2.2, ((BASE_MS / 1000) * 170) / trunk.len)) : 1;
    trunk.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = tr * boost;
    });
  }
  function eachAnim(fn: (anim: Animation) => void) {
    allSegs().forEach((segment) => {
      segment.packets.forEach((packet) => {
        if (packet.anim) fn(packet.anim);
      });
    });
  }
  function start() {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    allSegs().forEach(ensureAnims);
    setRates();
    /* only the sets in the combo run; hidden sets stay paused */
    CONNS.forEach((conn) => {
      const on = conn.on && !conn.down;
      setPackets(conn).forEach((packet) => {
        if (!packet.anim) return;
        if (on) {
          if (packet.anim.playState !== 'running') packet.anim.play();
        } else packet.anim.pause();
      });
    });
    const anyOn = active().length > 0;
    trunk.packets.forEach((packet) => {
      if (!packet.anim) return;
      if (anyOn) {
        if (packet.anim.playState !== 'running') packet.anim.play();
      } else packet.anim.pause();
    });
    sparksRun(true);
  }
  function stop() {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    eachAnim((anim) => anim.pause());
  }
  function drawStatic() {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    allSegs().forEach((mover) => {
      if (!mover.frames || !mover.parkIdx) return;
      mover.packets.forEach((packet, i) => {
        if (packet.anim) {
          packet.anim.cancel();
          packet.anim = null;
        }
        packet.el.style.transform = String(mover.frames![mover.parkIdx![i % mover.parkIdx!.length]!]!.transform);
      });
    });
  }
  /* ---------- run / visibility ---------- */
  function syncRun() {
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
      if (!stepTimer && !paused && !reduced.matches) schedule(touched ? USER_IDLE : 600);
    } else {
      stop();
      clearLater(stepTimer);
      stepTimer = 0;
    }
  }
  if ('IntersectionObserver' in window) {
    const near = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    near.observe(stage);
    const seen = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    seen.observe(stage);
    disposers.push(() => {
      near.disconnect();
      seen.disconnect();
    });
    later(() => {
      if (built || document.hidden) return;
      const r = stage.getBoundingClientRect();
      if (r.bottom > -320 && r.top < window.innerHeight + 320) buildStage();
    }, 1500);
  } else {
    visible = true;
    armMotion();
  }
  listen(document, 'visibilitychange', syncRun);
  let resizeRaf = 0;
  const scheduleLayout = () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => layout(false));
  };
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(scheduleLayout);
    resize.observe(stage);
    disposers.push(() => resize.disconnect());
  } else listen(window, 'resize', scheduleLayout);
  document.fonts?.ready.then(() => {
    if (!destroyed) scheduleLayout();
  });
  const onMotionPref = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  if (reduced.addEventListener) {
    reduced.addEventListener('change', onMotionPref);
    disposers.push(() => reduced.removeEventListener('change', onMotionPref));
  }
  /* ---------- boot ---------- */
  syncDerived();
  if (!reduced.matches) schedule(STEPS[0]!.wait);

  return () => {
    destroyed = true;
    timers.forEach((id) => window.clearTimeout(id));
    timers.clear();
    window.clearInterval(sparkTimer);
    cancelAnimationFrame(resizeRaf);
    cancelAnimationFrame(motionRaf);
    cancelAnimationFrame(pulseRaf);
    cancelAnimationFrame(snapRaf);
    eachAnim((anim) => anim.cancel());
    disposers.forEach((dispose) => dispose());
    svg.replaceChildren();
    pkLayer.replaceChildren();
    root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'is-offline', 'is-weak');
  };
}
